/**
 * Audio Device Manager
 *
 * Provides resilient audio device management for the application.
 * Handles:
 * - Monitoring audio device connections and disconnections
 * - Graceful recovery when devices are removed (Yealink headsets stealing audio)
 * - Automatic reconnection when devices reappear
 * - Computer sleep/wake recovery
 * - Persisting user device preferences across sessions
 * - Integration with Twilio Voice SDK audio device selection
 * - Fallback to default devices when preferred device unavailable
 */

import store from "@/store";
import { bus } from "@/main.js";

/** @type {AudioDeviceManager | null} */
let instance = null;

// How often to poll device list as a safety net (ms)
const DEVICE_POLL_INTERVAL = 3000;

// Delay after visibility change before recovering (ms)
const VISIBILITY_RECOVERY_DELAY = 1500;

// Delay for debouncing devicechange events (ms)
const DEVICE_CHANGE_DEBOUNCE = 150;

/**
 * Normalize device list for comparison (id + kind + label)
 */
function deviceFingerprint(device) {
	return `${device.deviceId}::${device.kind}::${device.groupId}`;
}

class AudioDeviceManager {
	/** @type {MediaDeviceInfo[]} */
	_audioInputs = [];

	/** @type {MediaDeviceInfo[]} */
	_audioOutputs = [];

	/** @type {string|null} preferred input deviceId (persisted) */
	_preferredInputId = null;

	/** @type {string|null} preferred output deviceId (persisted) */
	_preferredOutputId = null;

	/** @type {string|null} currently active input deviceId */
	_activeInputId = null;

	/** @type {string|null} currently active output deviceId */
	_activeOutputId = null;

	/** @type {number|null} polling interval handle */
	_pollTimer = null;

	/** @type {number|null} devicechange debounce handle */
	_debounceTimer = null;

	/** @type {boolean} */
	_initialized = false;

	/** @type {boolean} whether we are currently recovering */
	_recovering = false;

	/** @type {Function|null} reference to the bound devicechange handler */
	_boundOnDeviceChange = null;

	/** @type {Function|null} reference to the bound visibility handler */
	_boundOnVisibilityChange = null;

	/** @type {Set<string>} previous device fingerprints for change detection */
	_previousFingerprints = new Set();

	/** @type {object|null} reference to Twilio Voice.Device */
	_twilioDevice = null;

	/** @type {Set<WeakRef<HTMLAudioElement>>} tracked audio elements for reactive output routing */
	_trackedAudioElements = new Set();

	/** @type {WeakSet<HTMLAudioElement>} elements the user intends to be playing (for device-switch resume) */
	_playingElements = new WeakSet();

	// ─── Lifecycle ──────────────────────────────────────────────

	async initialize() {
		if (this._initialized) return;

		console.debug("[AudioDeviceManager] Initializing...");

		// Restore persisted preferences
		this._restorePreferences();

		// Request initial permission if needed (some browsers need getUserMedia before enumerateDevices returns labels)
		await this._ensurePermission();

		// Do initial device enumeration
		await this._enumerateDevices();

		// Set up event listeners
		this._boundOnDeviceChange = () => this._debouncedDeviceChange();
		navigator.mediaDevices.addEventListener("devicechange", this._boundOnDeviceChange);

		this._boundOnVisibilityChange = () => this._onVisibilityChange();
		document.addEventListener("visibilitychange", this._boundOnVisibilityChange);

		// Listen for online events (sometimes OS sleep triggers offline→online)
		window.addEventListener("online", () => this._onWake("online-event"));

		// Start safety-net polling
		this._startPolling();

		// Apply initial device selection
		await this._applyDeviceSelection();

		this._initialized = true;
		console.debug("[AudioDeviceManager] Initialized with inputs:", this._audioInputs.length, "outputs:", this._audioOutputs.length);
	}

	destroy() {
		console.debug("[AudioDeviceManager] Destroying...");

		if (this._boundOnDeviceChange) {
			navigator.mediaDevices.removeEventListener("devicechange", this._boundOnDeviceChange);
		}
		if (this._boundOnVisibilityChange) {
			document.removeEventListener("visibilitychange", this._boundOnVisibilityChange);
		}

		this._stopPolling();

		if (this._debounceTimer) {
			clearTimeout(this._debounceTimer);
		}

		this._initialized = false;
		instance = null;
	}

	// ─── Public API ─────────────────────────────────────────────

	/**
	 * Get the human-readable label for a device ID.
	 * When kind is specified, the corresponding list is searched first so that
	 * ambiguous IDs like "default" resolve to the correct device.
	 * @param {string} deviceId
	 * @param {"input"|"output"} [kind] - hint to search the right list first
	 * @returns {string} label like '"Yealink USB (abc123)"' or just '"abc123"'
	 */
	_deviceLabel(deviceId, kind) {
		if (!deviceId) return "(none)";

		const primary = kind === "input" ? this._audioInputs : kind === "output" ? this._audioOutputs : [];
		const secondary = kind === "input" ? this._audioOutputs : kind === "output" ? this._audioInputs : [...this._audioInputs, ...this._audioOutputs];
		const dev = primary.find(d => d.deviceId === deviceId) || secondary.find(d => d.deviceId === deviceId);

		return dev?.label ? `"${dev.label}" (${deviceId})` : `"${deviceId}"`;
	}

	/**
	 * Get available audio input devices
	 * @returns {MediaDeviceInfo[]}
	 */
	get audioInputs() {
		return this._audioInputs;
	}

	/**
	 * Get available audio output devices
	 * @returns {MediaDeviceInfo[]}
	 */
	get audioOutputs() {
		return this._audioOutputs;
	}

	/**
	 * Get the currently active input device ID
	 * @returns {string|null}
	 */
	get activeInputId() {
		return this._activeInputId;
	}

	/**
	 * Get the currently active output device ID
	 * @returns {string|null}
	 */
	get activeOutputId() {
		return this._activeOutputId;
	}

	/**
	 * Set the Twilio Voice Device reference for audio routing
	 * @param {object} device - Twilio Voice.Device instance
	 */
	setTwilioDevice(device) {
		this._twilioDevice = device;
		// Apply current device selections to the new Twilio device.
		// Use _applyToTwilio (best-effort) to avoid overwriting activeId/store.
		if (device) {
			this._applyToTwilio();
		}
	}

	/**
	 * Clear the Twilio Device reference (on cleanup)
	 */
	clearTwilioDevice() {
		this._twilioDevice = null;
	}

	/**
	 * Set preferred input device and apply it
	 * @param {string} deviceId
	 */
	async setInputDevice(deviceId) {
		console.debug("[AudioDeviceManager] Setting input device:", this._deviceLabel(deviceId, "input"));
		this._preferredInputId = deviceId;
		this._persistPreferences();
		await this._applyInputDevice(deviceId);
		this._syncToStore();
	}

	/**
	 * Set preferred output device and apply it
	 * @param {string} deviceId
	 */
	async setOutputDevice(deviceId) {
		console.debug("[AudioDeviceManager] Setting output device:", this._deviceLabel(deviceId, "output"));
		this._preferredOutputId = deviceId;
		this._persistPreferences();
		await this._applyOutputDevice(deviceId);
		await this._reapplyOutputToTrackedElements();
		this._syncToStore();
		bus.$emit("audio-output-changed", deviceId);
	}

	/**
	 * Force a re-check and recovery of audio devices.
	 * Useful to call after known disruption events.
	 */
	async recover() {
		if (this._recovering) return;
		this._recovering = true;

		console.debug("[AudioDeviceManager] Recovery triggered");

		try {
			await this._enumerateDevices();
			await this._applyDeviceSelection();
		} catch (err) {
			console.error("[AudioDeviceManager] Recovery failed:", err);
		} finally {
			this._recovering = false;
		}
	}

	/**
	 * Get a getUserMedia constraints object that targets the preferred/active input device.
	 * Falls back gracefully if the preferred device is unavailable.
	 * @param {object} [extraConstraints={}] - Additional audio constraints (e.g. echoCancellation)
	 * @returns {object} MediaStreamConstraints
	 */
	getInputConstraints(extraConstraints = {}) {
		const deviceId = this._preferredInputId || this._activeInputId;
		const audioConstraints = { ...extraConstraints };

		if (deviceId && deviceId !== "default") {
			audioConstraints.deviceId = { exact: deviceId };
		}

		return { audio: audioConstraints, video: false };
	}

	/**
	 * Wrapper around getUserMedia that automatically uses the preferred input device
	 * and handles device-lost errors by falling back to the default device.
	 * @param {object} [extraConstraints={}]
	 * @returns {Promise<MediaStream>}
	 */
	async getUserMedia(extraConstraints = {}) {
		const constraints = this.getInputConstraints(extraConstraints);

		try {
			return await navigator.mediaDevices.getUserMedia(constraints);
		} catch (err) {
			console.warn("[AudioDeviceManager] getUserMedia failed with preferred device, falling back to default:", err.message);

			// Fall back to default device
			const fallbackConstraints = {
				audio: { ...extraConstraints },
				video: false
			};

			return await navigator.mediaDevices.getUserMedia(fallbackConstraints);
		}
	}

	/**
	 * Apply the preferred output device to an HTMLAudioElement or Audio instance.
	 * Uses setSinkId when supported by the browser. Silently falls back if unsupported.
	 * The element is also tracked so that future output device changes are applied automatically.
	 * @param {HTMLAudioElement} audioElement - The audio element to route
	 * @returns {Promise<void>}
	 */
	async applyOutputDevice(audioElement) {
		if (!audioElement || typeof audioElement.setSinkId !== "function") {
			return;
		}

		// Track the element for reactive updates
		this._trackAudioElement(audioElement);

		// Use active output (already resolved to an available device).
		// Only fall back to preferred if active is not set yet (initial setup).
		const deviceId = this._activeOutputId || this._preferredOutputId;

		if (!deviceId) return;

		try {
			await audioElement.setSinkId(deviceId);
		} catch (err) {
			console.warn("[AudioDeviceManager] Failed to set output device on audio element:", err.message);
			// Fallback: reset to browser default sink
			if (deviceId !== "default" && deviceId !== "") {
				try {
					await audioElement.setSinkId("");
				} catch {
					// Browser will use its own default
				}
			}
		}
	}

	/**
	 * Stop tracking an audio element for reactive output updates.
	 * Call this when the element is destroyed/unmounted.
	 * @param {HTMLAudioElement} audioElement
	 */
	untrackAudioElement(audioElement) {
		for (const ref of this._trackedAudioElements) {
			if (ref.deref() === audioElement) {
				this._trackedAudioElements.delete(ref);
				break;
			}
		}
		this._playingElements.delete(audioElement);
	}

	/**
	 * Mark an audio element as "user-intends-playing".
	 * Called by components when the user initiates playback.
	 * Used by _emergencyResetTrackedSinks to know which elements to resume
	 * after an output device is lost (the browser auto-pauses them and we
	 * cannot rely on the component's `playing` state which may already be false).
	 * @param {HTMLAudioElement} audioElement
	 */
	markPlaying(audioElement) {
		if (audioElement) this._playingElements.add(audioElement);
	}

	/**
	 * Mark an audio element as "user-intends-paused".
	 * Called by components when the user pauses/stops playback or audio ends.
	 * @param {HTMLAudioElement} audioElement
	 */
	markPaused(audioElement) {
		if (audioElement) this._playingElements.delete(audioElement);
	}

	// ─── Private: Device Enumeration ────────────────────────────

	async _ensurePermission() {
		try {
			// Quick getUserMedia + stop to ensure we have permission for labels
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

			stream.getTracks().forEach(t => t.stop());
		} catch {
			// Permission denied or no devices — we'll handle this gracefully
			console.debug("[AudioDeviceManager] Could not acquire initial microphone permission");
		}
	}

	async _enumerateDevices() {
		try {
			const allDevices = await navigator.mediaDevices.enumerateDevices();

			this._audioInputs = allDevices.filter(d => d.kind === "audioinput");
			this._audioOutputs = allDevices.filter(d => d.kind === "audiooutput");

			// Update store
			this._syncToStore();

			// Check for device list changes
			const currentFingerprints = new Set(allDevices.filter(d => d.kind.startsWith("audio")).map(deviceFingerprint));
			const changed = !this._setsEqual(currentFingerprints, this._previousFingerprints);

			this._previousFingerprints = currentFingerprints;

			if (changed && this._initialized) {
				console.debug("[AudioDeviceManager] Device list changed. Inputs:", this._audioInputs.length, "Outputs:", this._audioOutputs.length);
				bus.$emit("audio-devices-changed", {
					inputs: this._audioInputs,
					outputs: this._audioOutputs
				});
			}

			return changed;
		} catch (err) {
			console.error("[AudioDeviceManager] Error enumerating devices:", err);
			return false;
		}
	}

	// ─── Private: Device Selection & Application ────────────────

	async _applyDeviceSelection() {
		const inputId = this._resolveInputDevice();
		const outputId = this._resolveOutputDevice();

		await this._applyInputDevice(inputId);
		await this._applyOutputDevice(outputId);
		await this._reapplyOutputToTrackedElements();

		this._syncToStore();
		bus.$emit("audio-output-changed", outputId);
	}

	/**
	 * Resolve which input device to use:
	 * 1. Preferred device if still available
	 * 2. Active device if still available
	 * 3. "default" device if available
	 * 4. First available device
	 * 5. "default"
	 */
	_resolveInputDevice() {
		if (this._preferredInputId && this._audioInputs.some(d => d.deviceId === this._preferredInputId)) {
			return this._preferredInputId;
		}
		if (this._activeInputId && this._audioInputs.some(d => d.deviceId === this._activeInputId)) {
			return this._activeInputId;
		}
		if (this._audioInputs.length > 0) {
			// Prefer "default" device, otherwise first
			const defaultDev = this._audioInputs.find(d => d.deviceId === "default");

			return defaultDev ? defaultDev.deviceId : this._audioInputs[0].deviceId;
		}
		return "default";
	}

	/**
	 * Resolve which output device to use (same logic as input)
	 */
	_resolveOutputDevice() {
		if (this._preferredOutputId && this._audioOutputs.some(d => d.deviceId === this._preferredOutputId)) {
			return this._preferredOutputId;
		}
		if (this._activeOutputId && this._audioOutputs.some(d => d.deviceId === this._activeOutputId)) {
			return this._activeOutputId;
		}
		if (this._audioOutputs.length > 0) {
			const defaultDev = this._audioOutputs.find(d => d.deviceId === "default");

			return defaultDev ? defaultDev.deviceId : this._audioOutputs[0].deviceId;
		}
		return "default";
	}

	async _applyInputDevice(deviceId) {
		if (!deviceId) return;

		// Always set the active ID to the intended device, regardless of Twilio outcome.
		// This ensures the user's selection is never silently overwritten.
		this._activeInputId = deviceId;

		// Update mic status in store
		const hasMic = this._audioInputs.length > 0;

		store.commit("voice/setIsMicroOn", hasMic);

		// Best-effort: route through Twilio
		await this._setTwilioInput(deviceId);
	}

	async _applyOutputDevice(deviceId) {
		if (!deviceId) return;

		// Always set the active ID to the intended device
		this._activeOutputId = deviceId;

		// Best-effort: route through Twilio
		await this._setTwilioOutput(deviceId);
	}

	/**
	 * Best-effort: route input to Twilio Device.
	 * Falls back to "default" on the Twilio side only — never changes _activeInputId.
	 */
	async _setTwilioInput(deviceId) {
		if (!this._twilioDevice?.audio) return;

		try {
			await this._twilioDevice.audio.setInputDevice(deviceId);
			console.debug("[AudioDeviceManager] Twilio input device set to:", this._deviceLabel(deviceId, "input"));
		} catch (err) {
			console.warn("[AudioDeviceManager] Failed to set Twilio input device:", err.message);
			if (deviceId !== "default") {
				try {
					await this._twilioDevice.audio.setInputDevice("default");
					console.debug("[AudioDeviceManager] Twilio input fell back to default");
				} catch {
					// Twilio can't route anything — it will use its own default
				}
			}
		}
	}

	/**
	 * Best-effort: route output to Twilio Device.
	 * Falls back to "default" on the Twilio side only — never changes _activeOutputId.
	 */
	async _setTwilioOutput(deviceId) {
		if (!this._twilioDevice?.audio) return;

		try {
			await this._twilioDevice.audio.speakerDevices.set(deviceId);
			console.debug("[AudioDeviceManager] Twilio output device set to:", this._deviceLabel(deviceId, "output"));
		} catch (err) {
			console.warn("[AudioDeviceManager] Failed to set Twilio output device:", err.message);
			if (deviceId !== "default") {
				try {
					await this._twilioDevice.audio.speakerDevices.set("default");
					console.debug("[AudioDeviceManager] Twilio output fell back to default");
				} catch {
					// Twilio can't route anything
				}
			}
		}
	}

	/**
	 * Best-effort: apply current device selections to the Twilio Device
	 * without touching _activeInputId / _activeOutputId or the store.
	 * Called when a new Twilio Device is attached.
	 */
	async _applyToTwilio() {
		// Use the resolution helpers so we never send a stale/disconnected
		// preferred ID to Twilio — they validate against the current device list.
		const inputId = this._resolveInputDevice();
		const outputId = this._resolveOutputDevice();

		await this._setTwilioInput(inputId);
		await this._setTwilioOutput(outputId);
	}

	// ─── Private: Event Handlers ────────────────────────────────

	_debouncedDeviceChange() {
		if (this._debounceTimer) {
			clearTimeout(this._debounceTimer);
		}

		this._debounceTimer = setTimeout(async () => {
			await this._handleDeviceChange();
		}, DEVICE_CHANGE_DEBOUNCE);
	}

	async _handleDeviceChange() {
		const changed = await this._enumerateDevices();

		if (!changed) return;

		// Check if our active input device was removed
		const inputLost = this._activeInputId &&
			this._activeInputId !== "default" &&
			!this._audioInputs.some(d => d.deviceId === this._activeInputId);

		// Check if our active output device was removed
		const outputLost = this._activeOutputId &&
			this._activeOutputId !== "default" &&
			!this._audioOutputs.some(d => d.deviceId === this._activeOutputId);

		if (inputLost || outputLost) {
			console.warn("[AudioDeviceManager] Active device(s) lost — input:", inputLost ? this._deviceLabel(this._activeInputId, "input") : false, "output:", outputLost ? this._deviceLabel(this._activeOutputId, "output") : false);

			if (outputLost) {
				// Force macOS CoreAudio to activate the speakers immediately
				// by playing a silent AudioContext signal — without this,
				// the OS can take 5-10 s to route audio to the new output.
				await this._wakeUpAudioOutput();

				// Fast-path: reset all tracked audio elements to the browser
				// default sink so sound resumes before the full resolution chain.
				await this._emergencyResetTrackedSinks();
			}

			bus.$emit("audio-device-lost", { inputLost, outputLost });
		}

		// Check if our preferred device came back
		const preferredInputBack = this._preferredInputId &&
			this._preferredInputId !== this._activeInputId &&
			this._audioInputs.some(d => d.deviceId === this._preferredInputId);

		const preferredOutputBack = this._preferredOutputId &&
			this._preferredOutputId !== this._activeOutputId &&
			this._audioOutputs.some(d => d.deviceId === this._preferredOutputId);

		if (preferredInputBack || preferredOutputBack) {
			console.debug("[AudioDeviceManager] Preferred device reconnected — input:", preferredInputBack ? this._deviceLabel(this._preferredInputId, "input") : false, "output:", preferredOutputBack ? this._deviceLabel(this._preferredOutputId, "output") : false);
			bus.$emit("audio-device-reconnected", { preferredInputBack, preferredOutputBack });
		}

		// Re-apply device selection (will pick preferred if available, or fallback)
		await this._applyDeviceSelection();
	}

	_onVisibilityChange() {
		if (document.visibilityState === "visible") {
			console.debug("[AudioDeviceManager] Tab became visible — scheduling recovery");
			setTimeout(() => this._onWake("visibility-change"), VISIBILITY_RECOVERY_DELAY);
		}
	}

	async _onWake(source) {
		console.debug(`[AudioDeviceManager] Wake recovery triggered by: ${source}`);
		await this.recover();

		// If on a call, re-assert the mic stream
		if (store.getters["voice/isOnCall"] && store.getters["voice/isMicroOn"]) {
			try {
				console.debug("[AudioDeviceManager] Re-enabling microphone after wake");
				store.commit("voice/enableMicrophone");
			} catch (err) {
				console.error("[AudioDeviceManager] Failed to re-enable mic after wake:", err);
			}
		}
	}

	// ─── Private: Polling ───────────────────────────────────────

	_startPolling() {
		this._stopPolling();
		this._pollTimer = setInterval(async () => {
			try {
				await this._enumerateDevices();

				// Verify active devices are still valid
				const inputValid = !this._activeInputId ||
					this._activeInputId === "default" ||
					this._audioInputs.some(d => d.deviceId === this._activeInputId);

				const outputValid = !this._activeOutputId ||
					this._activeOutputId === "default" ||
					this._audioOutputs.some(d => d.deviceId === this._activeOutputId);

				if (!inputValid || !outputValid) {
					console.debug("[AudioDeviceManager] Poll detected stale device, recovering...");
					await this._applyDeviceSelection();
				}
			} catch (err) {
				console.error("[AudioDeviceManager] Poll error:", err);
			}
		}, DEVICE_POLL_INTERVAL);
	}

	_stopPolling() {
		if (this._pollTimer) {
			clearInterval(this._pollTimer);
			this._pollTimer = null;
		}
	}

	// ─── Private: Persistence ───────────────────────────────────

	_persistPreferences() {
		try {
			const prefs = {
				inputId: this._preferredInputId,
				outputId: this._preferredOutputId
			};

			localStorage.setItem("fresk_audio_preferences", JSON.stringify(prefs));
		} catch {
			// localStorage may not be available
		}
	}

	_restorePreferences() {
		try {
			const raw = localStorage.getItem("fresk_audio_preferences");

			if (raw) {
				const prefs = JSON.parse(raw);

				this._preferredInputId = prefs.inputId || null;
				this._preferredOutputId = prefs.outputId || null;
				console.debug("[AudioDeviceManager] Restored preferences — input:", this._deviceLabel(this._preferredInputId, "input"), "output:", this._deviceLabel(this._preferredOutputId, "output"));
			}
		} catch {
			// Ignore parse errors
		}
	}

	// ─── Public: Re-sync ────────────────────────────────────────

	/**
	 * Re-sync the AudioDeviceManager state to the Vuex store.
	 * Call this after login or whenever the store has been cleared
	 * (e.g. logout / session expiry) so the persisted preferences
	 * are pushed back into the reactive Vuex state.
	 */
	async resync() {
		this._restorePreferences();
		await this._enumerateDevices();
		await this._applyDeviceSelection();
		console.debug("[AudioDeviceManager] Resynced to store — input:", this._deviceLabel(this._activeInputId, "input"), "output:", this._deviceLabel(this._activeOutputId, "output"));
	}

	// ─── Private: Store Sync ────────────────────────────────────

	_syncToStore() {
		store.commit("voice/setAudioDevices", {
			audioInputs: this._audioInputs,
			audioOutputs: this._audioOutputs,
			activeInputId: this._preferredInputId || this._activeInputId,
			activeOutputId: this._preferredOutputId || this._activeOutputId
		});
	}

	// ─── Private: Utility ───────────────────────────────────────

	_setsEqual(a, b) {
		if (a.size !== b.size) return false;
		for (const item of a) {
			if (!b.has(item)) return false;
		}
		return true;
	}

	// ─── Private: Audio Element Tracking ────────────────────────

	/**
	 * Track an audio element (via WeakRef) for reactive output device updates.
	 * Automatically cleans up garbage-collected refs.
	 * @param {HTMLAudioElement} audioElement
	 */
	_trackAudioElement(audioElement) {
		// Check if already tracked
		for (const ref of this._trackedAudioElements) {
			if (ref.deref() === audioElement) return;
		}

		// Purge stale refs while we're here
		for (const ref of this._trackedAudioElements) {
			if (!ref.deref()) {
				this._trackedAudioElements.delete(ref);
			}
		}

		this._trackedAudioElements.add(new WeakRef(audioElement));
	}

	/**
	 * Emergency fast-path: immediately reset all tracked audio elements to the
	 * browser default sink ("") so that sound resumes as quickly as possible
	 * when the active output device disappears.  After this, the normal
	 * resolution chain will set the correct resolved device ID.
	 */
	async _emergencyResetTrackedSinks() {
		for (const ref of this._trackedAudioElements) {
			const el = ref.deref();

			if (!el) {
				this._trackedAudioElements.delete(ref);
				continue;
			}

			if (typeof el.setSinkId === "function") {
				try {
					await el.setSinkId("");

					// Only resume elements the user intended to be playing.
					// The browser auto-pauses elements when a device disconnects,
					// and Howler/component state may already reflect that pause.
					// _playingElements is set by components on user actions only.
					if (this._playingElements.has(el)) {
						await el.play();
						el.currentTime = el.currentTime; // kick the audio pipeline
						console.debug("[AudioDeviceManager] Emergency: reset sink + resumed playing element");
					} else {
						console.debug("[AudioDeviceManager] Emergency: reset sink for paused element");
					}
				} catch (err) {
					console.warn("[AudioDeviceManager] Emergency sink reset failed:", err.message);
				}
			}
		}
	}

	/**
	 * Force macOS / the OS audio subsystem to activate the default output
	 * by playing a brief silent signal through a fresh AudioContext.
	 * Without this, macOS can take 5-10 seconds to route audio to speakers
	 * after a Bluetooth device (e.g. AirPods) disconnects.
	 */
	async _wakeUpAudioOutput() {
		try {
			const ctx = new AudioContext();

			// If the context is suspended (autoplay policy), resume it
			if (ctx.state === "suspended") {
				await ctx.resume();
			}

			// Create a silent oscillator to force the OS to initialise the output device
			const oscillator = ctx.createOscillator();
			const gain = ctx.createGain();

			gain.gain.value = 0; // completely silent
			oscillator.connect(gain);
			gain.connect(ctx.destination);
			oscillator.start();

			// Let it run briefly so the OS sees active audio output
			await new Promise(r => setTimeout(r, 80));

			oscillator.stop();
			await ctx.close();
			console.debug("[AudioDeviceManager] Audio output wake-up completed");
		} catch (err) {
			console.warn("[AudioDeviceManager] Audio output wake-up failed:", err.message);
		}
	}

	/**
	 * Re-apply the current output device to all tracked audio elements.
	 * Called when the output device selection changes.
	 */
	async _reapplyOutputToTrackedElements() {
		// Use the already-resolved active output (which accounts for device availability),
		// NOT the preferred ID which may point to a disconnected device.
		const deviceId = this._activeOutputId || "default";

		for (const ref of this._trackedAudioElements) {
			const el = ref.deref();

			if (!el) {
				this._trackedAudioElements.delete(ref);
				continue;
			}

			if (typeof el.setSinkId === "function") {
				try {
					await el.setSinkId(deviceId);
				} catch (err) {
					console.warn("[AudioDeviceManager] Failed to re-apply output device to tracked element:", err.message);
					// Fallback: reset to browser default sink
					if (deviceId !== "default" && deviceId !== "") {
						try {
							await el.setSinkId("");
						} catch {
							// Browser will use its own default
						}
					}
				}
			}
		}

		console.debug(`[AudioDeviceManager] Re-applied output device ${this._deviceLabel(deviceId, "output")} to ${this._trackedAudioElements.size} tracked element(s)`);
	}
}

/**
 * Get or create the singleton AudioDeviceManager instance
 * @returns {AudioDeviceManager}
 */
export function getAudioDeviceManager() {
	if (!instance) {
		instance = new AudioDeviceManager();
	}
	return instance;
}

export default getAudioDeviceManager;
